import React from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import Big from 'big.js'

// Shared foundation for the public plugin API, used by both the v6 and v7
// donation widgets. Everything a partner plugin depends on lives on
// `window.Pledge`, so a plugin is written once and runs on either version:
//
//   window.Pledge = {
//     version,                        // 6 | 7 — for the rare plugin that must branch
//     widget: { addPlugin(plugin) },  // register a plugin (queued until mount)
//     components: { InputField, … },  // version's React UI (injected by the pack)
//     lib: { Big, … },                // version-agnostic utilities (owned here)
//   }
//
// This module owns the version-agnostic pieces (the queue, `lib`, the error
// boundary, and the data/errors façades). Version-specific React components
// can't be imported here — v6 is Bootstrap, v7 is Tailwind — so each version's
// pack injects its own set via installPledge({ components }).

// Utilities we guarantee to plugins regardless of widget version.
const sharedLib = { Big }

// The widget-facing registration queue. Plugins register at parse time — their
// <script>s run before the widget mounts — so addPlugin just appends to
// `entries`, which the widget drains on mount. Each entry gets an auto-assigned
// id so widget state keys off a stable handle rather than array position (and
// so no author-supplied unique name is required).
//
// A plugin declares which widget version it targets via `plugin.version`. The
// widget — not the snippet — accepts or rejects it: v6 accepts `6` or a
// version-less (legacy, pre-Pledge) plugin; v7 requires an explicit `7`. So one
// snippet can register both a `{ version: 6 }` and a `{ version: 7 }` plugin
// (e.g. for the EE-wallet case, where the same page renders v6 or v7 depending
// on wallet availability) and each widget renders only its match — safer than
// branching on Pledge.version inside plugin code. A non-matching plugin is
// silently skipped (the intended outcome of dual registration).
const createWidgetApi = version => {
  let seq = 0
  const entries = []
  const accepts = target => target === version || (version === 6 && target == null)

  return {
    entries,
    addPlugin(plugin) {
      if (!plugin || typeof plugin != 'object') {
        console.error('Pledge.widget.addPlugin: expected a plugin object, got', plugin)
        return
      }
      if (!accepts(plugin.version)) return
      entries.push({ id: seq++, plugin })
    },
  }
}

// Called once per page by the widget's pack, before any plugin <script> runs.
// Merge-friendly across repeat calls: `components` and `lib` merge. `version`
// and `widget` are first-write-wins so Pledge.version always agrees with the
// accept/reject gate baked into `widget` (which closes over the first install's
// version). One pack installs per page — set_entry_points picks exactly one —
// so a second install with a *different* version can't happen today; the
// first-wins rule just keeps version and the gate from disagreeing if it ever
// could. Returns the widget API so the pack/app can read `entries`.
export const installPledge = ({ version, components = {} }) => {
  const pledge = (window.Pledge ??= {})
  pledge.version ??= version
  pledge.widget ??= createWidgetApi(version)
  pledge.components = { ...pledge.components, ...components }
  pledge.lib = { ...sharedLib, ...pledge.lib }
  return pledge.widget
}

// onError handler for a plugin's boundary. Always logs (console + Honeybadger);
// for a non-staff donor it also calls `disable()` so a broken partner plugin
// drops out of checkout instead of blocking it — staff keep it rendered (via a
// fallback) so they can see the failure.
export const pluginErrorHandler = ({ session, disable } = {}) => (error, info) => {
  console.error(error, info)
  if (typeof Honeybadger != 'undefined') Honeybadger.notify(error, { context: info })
  if (!session?.staff) disable?.()
}

// Wraps a plugin's rendered hook so a throw can't take down the widget. The
// fallback is injected (default: render nothing) because its styling is
// version-specific — the version passes a staff-gated, version-styled notice.
export const PluginErrorBoundary = ({ onError, fallback = () => null, children }) =>
  <ErrorBoundary onError={onError} fallbackRender={fallback}>
    {children}
  </ErrorBoundary>

// A plugin's scoped data bag: the raw dataset spread for reads, plus get/set.
// `set` clears any error on that key (so a field error clears as the donor
// fixes it) then updates via the version-provided setter. Reads are pure — no
// side effects (v6's get() used to trigger a setState, which we drop here).
export const pluginData = (dataset = {}, { setData, clearError } = {}) => ({
  ...dataset,
  get: (key, fallback = '') => dataset?.[key] ?? fallback,
  set: (key, value) => {
    clearError?.(key)
    setData?.(prev => ({ ...prev, [key]: value }))
  },
})

// A plugin's scoped errors bag: the raw error hash plus firstFor(key) and
// resetFor(key). The helpers are non-enumerable so they don't leak into a
// spread of the errors object.
export const pluginErrors = (errorSet = {}, { setErrors } = {}) => {
  const errors = { ...errorSet }
  Object.defineProperty(errors, 'firstFor', {
    value: key => errorSet?.[key]?.[0],
  })
  Object.defineProperty(errors, 'resetFor', {
    value: key => setErrors?.(prev => ({ ...prev, [key]: null })),
  })
  return errors
}
